import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Brain, TrendingUp, TrendingDown, Minus, Loader2, Target, CheckCircle, Zap, Star, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentAISummary = ({ studentId, studentName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const fetchProgress = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/teacher/student/${studentId}/ai-summary`);
      setData(res.data);
      setFetched(true);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [studentId]);

  if (!fetched) {
    return (
      <Card className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-indigo-600" />
            <div>
              <p className="font-semibold text-sm text-indigo-900">AI Progress Report</p>
              <p className="text-xs text-indigo-500">View detailed progress analysis for {studentName}</p>
            </div>
          </div>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={fetchProgress} disabled={loading} data-testid={`load-progress-${studentId}`}>
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Brain className="w-4 h-4 mr-1" />}
            {loading ? 'Analyzing...' : 'Generate Report'}
          </Button>
        </div>
      </Card>
    );
  }

  if (!data?.ai_summary) {
    return (
      <Card className="p-4 text-center text-gray-400 text-sm">
        <Brain className="w-8 h-8 mx-auto mb-2 opacity-30" />
        Not enough data to generate progress report for {studentName}
      </Card>
    );
  }

  const ai = data.ai_summary;
  const trendIcon = ai.overall_trend === 'improving' ? <TrendingUp className="w-4 h-4 text-green-500" /> : ai.overall_trend === 'declining' ? <TrendingDown className="w-4 h-4 text-red-500" /> : <Minus className="w-4 h-4 text-gray-400" />;
  const trendColor = ai.overall_trend === 'improving' ? 'border-green-300 bg-green-50' : ai.overall_trend === 'declining' ? 'border-red-300 bg-red-50' : 'border-blue-300 bg-blue-50';

  return (
    <div className="space-y-4" data-testid={`progress-view-${studentId}`}>
      {/* Header Card */}
      <Card className={`p-4 border-2 ${trendColor}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Brain className="w-5 h-5 text-indigo-600" />
            <div>
              <p className="font-semibold text-sm">{studentName} — AI Progress Report</p>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant="outline" className="text-xs">{data.overall_average}% avg</Badge>
                <div className="flex items-center gap-1">{trendIcon}<span className="text-xs capitalize font-medium">{ai.overall_trend}</span></div>
                <Badge className={`text-xs ${ai.engagement_level === 'high' ? 'bg-green-100 text-green-700' : ai.engagement_level === 'low' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{ai.engagement_level} engagement</Badge>
              </div>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={fetchProgress} disabled={loading} className="text-xs">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
          </Button>
        </div>
        <p className="text-sm text-gray-700">{ai.summary}</p>
      </Card>

      {/* Score Bar */}
      <div className="px-2">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Overall Performance</span>
          <span className="font-semibold">{data.overall_average}%</span>
        </div>
        <Progress value={data.overall_average} className="h-3 rounded-full" />
      </div>

      {/* Three Column Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Strengths */}
        {ai.key_strengths?.length > 0 && (
          <Card className="p-3 border-green-100">
            <h4 className="text-xs font-semibold text-green-700 flex items-center gap-1 mb-2"><Star className="w-3.5 h-3.5" /> Strengths</h4>
            <ul className="space-y-1">{ai.key_strengths.map((s, i) => (
              <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5"><CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />{s}</li>
            ))}</ul>
          </Card>
        )}

        {/* Areas Needing Attention */}
        {ai.areas_needing_attention?.length > 0 && (
          <Card className="p-3 border-amber-100">
            <h4 className="text-xs font-semibold text-amber-700 flex items-center gap-1 mb-2"><Target className="w-3.5 h-3.5" /> Needs Attention</h4>
            <ul className="space-y-1">{ai.areas_needing_attention.map((a, i) => (
              <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5"><AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />{a}</li>
            ))}</ul>
          </Card>
        )}

        {/* Recommendations */}
        {ai.recommendations?.length > 0 && (
          <Card className="p-3 border-indigo-100">
            <h4 className="text-xs font-semibold text-indigo-700 flex items-center gap-1 mb-2"><Zap className="w-3.5 h-3.5" /> Recommendations</h4>
            <ul className="space-y-1">{ai.recommendations.map((r, i) => (
              <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5"><ArrowUp className="w-3 h-3 text-indigo-400 mt-0.5 flex-shrink-0" />{r}</li>
            ))}</ul>
          </Card>
        )}
      </div>
    </div>
  );
};

export default StudentAISummary;
